import { arrayEquals } from "./array"

export const keyLoader = (data, keys) => {
  if (!data) {
    return null
  }

  for (const key of keys) {
    if (key.includes(".")) {
      const [newKey, ...rest] = key.split(".")
      if (data[newKey] != null) {
        const newValue = keyLoader(data[newKey], rest)
        if (newValue != null) {
          return newValue
        }
      }
    }

    if (data[key] != null) {
      return data[key]
    }
  }
}

export function isObject(variable) {
  return typeof variable === "object" && variable !== null && !Array.isArray(variable)
}

export const objectEqual = (o1, o2) => {
  if (!isObject(o1) || !isObject(o2)) return false

  const k1 = Object.keys(o1)
  const k2 = Object.keys(o2)
  if (!arrayEquals(k1, k2)) return false

  const v1 = Object.values(o1)
  const v2 = Object.values(o2)
  return arrayEquals(v1, v2)
}

export const isEmptyObject = obj => {
  return obj == null || !Object.keys(obj).length
}

export const copyUrlSearchParams = (old, options = { keysToSkip: null }) => {
  const keysToSkip = options?.keysToSkip ?? []
  let keys = Array.from(old.keys())

  if (keysToSkip.length > 0) {
    keys = keys.filter(key => !keysToSkip.includes(key))
  }

  return keys.reduce((newDictionary, key) => {
    const values = old.getAll(key)
    newDictionary[key] = values.length === 1 ? values[0] : values
    return newDictionary
  }, {})
}

/**
 * @typedef {Object} BuildIdLookupOptions
 * @property {string} key - the key to use for the lookup
 */

/**
 * Build a lookup based on a property. if no key in options is specified, 'id' will be used
 *
 * @param {Array<Object>|null} data - The data to build a lookup around
 * @param {BuildIdLookupOptions} [options] - builder options
 * @returns {Object.<string, Object>} the lookup dict by property
 */
export const buildIdLookup = (data, options) => {
  if (data == null) {
    return {}
  }

  const key = options?.key ?? "id"

  return data
    .map(d => [d[key], d])
    .reduce((data, [id, value]) => {
      data[id] = value
      return data
    }, {})
}

/**
 * Builds a reverse lookup from values to keys
 *
 * @param {Object.<string, string>} data - Object to reverse
 * @returns {Object.<string, string>} Reversed lookup object
 */
export const buildLookup = data => {
  return Object.entries(data)
    .map(([key, value]) => [value, key])
    .reduce((data, [key, value]) => {
      data[key] = value
      return data
    }, {})
}

/**
 * Groups array elements by a key function (compatible with Node.js 18+)
 * Provides the same functionality as Object.groupBy but with broader compatibility
 *
 * @template T
 * @param {Array<T>} array - Array to group
 * @param {function(T): string} keyFn - Function that returns grouping key for each element
 * @returns {Object.<string, Array<T>>} Object with keys from keyFn and arrays of grouped elements
 *
 * @example
 * const data = [
 *   { type: 'a', value: 1 },
 *   { type: 'b', value: 2 },
 *   { type: 'a', value: 3 }
 * ]
 * const grouped = groupBy(data, item => item.type)
 * // Result: { a: [{type: 'a', value: 1}, {type: 'a', value: 3}], b: [{type: 'b', value: 2}] }
 */
export const groupBy = (array, keyFn) => {
  if (!Array.isArray(array)) {
    return {}
  }

  return array.reduce((acc, item) => {
    const key = keyFn(item)
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})
}
